import type { ItemFieldRow } from './item-field-reader';
import type { AbilityEssenceSourceDependencies } from './ability-essence-sources';
import { createAncestralEssenceSourceCollector } from './ancestral-essence-sources';
import { createFormulaReadContext } from './formula-read-context';
import { scoreCarrierIntegerLayer } from './formula-variable-resolution';
import { phpJsonFloat, isPhpNumeric } from './php-decimal-values';
import { phpJsonString } from './php-json-values';
import { phpJsonInteger, phpAdd } from './php-integer-arithmetic';
import type { PhpArithmetic } from './php-integer-arithmetic';
import { integerCompileWorkingRow, integerCompileResult, putIntegerCompilePath, IntegerComputeSlotWriteError } from './integer-compute-slots';

type Row = Record<string, unknown>;
const arrayLike = (v: unknown): v is ItemFieldRow => v !== null && typeof v === 'object';
const own = (v: unknown, key: string): unknown => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, key) ? (v as Row)[key]
    : typeof v === 'string' && key === '0' ? v[0] : undefined;
const aboveZero = (v: unknown): boolean => arrayLike(v) ? true : typeof v === 'string' && !isPhpNumeric(v) ? v > '0' : Number(v) > 0;
/** Actual calculate_preliminary_essence. The starting root value is installed
 * before ancestry/background formulas read the host. Positive input overrides
 * apply after the cap. Caller-level registry/fortune/spirit ordering is separate.
 */
export function createPreliminaryEssenceCompiler(deps: AbilityEssenceSourceDependencies) {
    const sourceDeps = {...deps, trace: (e: readonly unknown[]): void => {
        // Some readers report rows that this stage subsequently mutates.
        deps.trace?.(integerCompileResult(integerCompileWorkingRow(e)) as readonly unknown[]);
    }};
    const ancestry = createAncestralEssenceSourceCollector(sourceDeps), readers = createFormulaReadContext(sourceDeps);
    return (input: ItemFieldRow | null | false, rawName: unknown): ItemFieldRow => {
        if (arrayLike(rawName)) throw new TypeError('Illegal essence offset');
        const name = phpJsonString(rawName), key = rawName == null ? '' : typeof rawName === 'boolean' || typeof rawName === 'number' ? String(phpJsonInteger(rawName)) : name;
        if (input !== null && input !== false && !arrayLike(input)) {
            if (typeof input === 'string') throw new TypeError('Cannot access offset of type string on string');
            throw new IntegerComputeSlotWriteError('Cannot use scalar value as an array');
        }
        const host = integerCompileWorkingRow(input === null || input === false ? {} : input);
        const pc = own(host, 'player_characters');
        const base = phpJsonFloat(own(pc, name + '_input_default')), other = phpJsonFloat(own(pc, name + '_input_other'));
        putIntegerCompilePath(host, [key, '0'], base + other);
        const ancestral = ancestry.direct(host, 'ancestral_' + name).rolled_total;
        const backgroundKey = 'background_' + name, background = scoreCarrierIntegerLayer(host, backgroundKey, 0, false, deps.metadata, readers);
        putIntegerCompilePath(host, [backgroundKey, '0'], background);
        const start = Number(own(own(host, key), '0'));
        let value = Math.min(Math.max(5, start), start + ancestral + phpJsonFloat(own(own(host, backgroundKey), '0')));
        const override = own(pc, name + '_input_override'); if (override != null && aboveZero(override)) value = phpJsonFloat(override);
        putIntegerCompilePath(host, [key, '0'], value);
        return integerCompileResult(host);
    };
}

export interface PcPreliminaryEssenceDependencies extends AbilityEssenceSourceDependencies {
    metadata: Omit<AbilityEssenceSourceDependencies['metadata'], 'classes'> & {
        classes: readonly {variable_name: string; item_id: string; caster_type: string}[];
        allEssences: readonly {variable_name: string; item_id: string}[];
        fullCasterType: string; halfCasterType: string;
    };
    onStage?: (name: string, before: ItemFieldRow, after: ItemFieldRow) => void;
}
/** PC branch of finalize_creature_arr, including full + ceil(combined-half/2)
 * class levels and the second fortune/spirit calculations. The caller owns the
 * player-character eligibility gate; this factory implements its branch body.
 */
export function createPcPreliminaryEssenceCompiler(deps: PcPreliminaryEssenceDependencies) {
    const calculate = createPreliminaryEssenceCompiler(deps), readers = createFormulaReadContext(deps);
    const clone = (row: ItemFieldRow): ItemFieldRow => integerCompileResult(integerCompileWorkingRow(row));
    function spellLevel(input: ItemFieldRow): ItemFieldRow {
        if (!arrayLike(input)) throw new TypeError('Spellcasting level requires a PHP array');
        const host = integerCompileWorkingRow(input), pc = own(host, 'player_characters'); let full: PhpArithmetic = 0n, half: PhpArithmetic = 0n;
        for (let slot = 1; slot <= 20; slot++) {
            const node = own(pc, 'class_' + slot);
            const id = arrayLike(node) ? readers.itemField(node, 'item_id') ?? own(node, '0') ?? '' : phpJsonString(node ?? '');
            if (id === '') continue;
            if (arrayLike(id)) throw new TypeError('Class ID requires a scalar string');
            const key = phpJsonString(id); if (!/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(key)) continue;
            const raw = own(pc, 'class_level_' + slot), level = phpJsonInteger(arrayLike(raw) && Object.prototype.hasOwnProperty.call(raw, '0') ? own(raw, '0') : raw);
            const info = deps.metadata.classes.find(c => c.item_id === key); if (!info) continue;
            if (info.caster_type === deps.metadata.fullCasterType) full = phpAdd(full, level);
            else if (info.caster_type === deps.metadata.halfCasterType) half = phpAdd(half, level);
        }
        putIntegerCompilePath(host, ['spell_casting_level', '0'], Number(full) + Math.ceil(Number(half) / 2));
        return integerCompileResult(host);
    }
    function pcSequence(input: ItemFieldRow): ItemFieldRow {
        if (!arrayLike(input)) throw new TypeError('PC essence branch requires a PHP array');
        let host = clone(input);
        const step = (name: string): void => { const before = deps.onStage ? clone(host) : null; host = calculate(host, name); if (before) deps.onStage?.(name, before, clone(host)); };
        for (const essence of deps.metadata.allEssences) step(essence.variable_name);
        let work = integerCompileWorkingRow(host); putIntegerCompilePath(work, ['player_characters', 'fortune_input_default'], 2); host = integerCompileResult(work); step('fortune');
        if (readers.layerValue(host, 'spellcasting_essence') !== null) host = spellLevel(host);
        work = integerCompileWorkingRow(host); putIntegerCompilePath(work, ['player_characters', 'spirit_input_default'], readers.layerValue(host, 'spell_casting_level') ?? 0); host = integerCompileResult(work); step('spirit');
        return host;
    }
    return {spellLevel, pcSequence};
}
